import React, { createContext, useCallback, useContext, useEffect, useState } from "react";

const SETTINGS_ENDPOINT = "/api/settings";

export interface SiteSettings {
  site_name: string;
  site_tagline: string;
  contact_email: string;
  contact_phone: string;
  contact_address: string;
  social_twitter: string;
  social_linkedin: string;
  social_github: string;
  hero_headline: string;
  hero_subtext: string;
  cta_primary_text: string;
  cta_secondary_text: string;
  theme_preset: string;
  footer_tagline: string;
  seo_title: string;
  seo_description: string;
  og_image_url: string;
  maintenance_mode: string;
  banner_message: string;
  banner_enabled: string;
  [key: string]: string;
}

const defaults: SiteSettings = {
  site_name: "9nd.org",
  site_tagline: "Powerful Tools, Simplified",
  contact_email: "info@9nd.org",
  contact_phone: "",
  contact_address: "",
  social_twitter: "https://twitter.com/9ndorg",
  social_linkedin: "https://linkedin.com/company/9nd-org",
  social_github: "https://github.com/9nd-org",
  hero_headline: "Professional tools, without the complexity.",
  hero_subtext:
    "12 AI-powered tools across finance, analytics, marketing, design, development, and productivity. Get results in seconds — not hours.",
  cta_primary_text: "Get Started Free",
  cta_secondary_text: "Browse All Tools →",
  theme_preset: "indigo",
  footer_tagline:
    "Professional tools for modern workflows. Fast, private, and no fluff.",
  seo_title: "9nd.org — Powerful Tools, Simplified",
  seo_description:
    "Run professional finance, design, analytics, and marketing tools instantly — no setup required.",
  og_image_url: "/assets/brand/og-image.png",
  maintenance_mode: "false",
  banner_message: "",
  banner_enabled: "false",
};

const THEME_VARS: Record<string, { from: string; to: string; btn: string; btnHover: string; ring: string }> = {
  indigo:  { from: "#4f46e5", to: "#7c3aed", btn: "#4f46e5", btnHover: "#4338ca", ring: "rgba(99,102,241,0.4)" },
  teal:    { from: "#0d9488", to: "#0891b2", btn: "#0d9488", btnHover: "#0f766e", ring: "rgba(20,184,166,0.4)" },
  rose:    { from: "#e11d48", to: "#be185d", btn: "#e11d48", btnHover: "#be123c", ring: "rgba(225,29,72,0.4)" },
  amber:   { from: "#d97706", to: "#ea580c", btn: "#d97706", btnHover: "#b45309", ring: "rgba(217,119,6,0.4)" },
  emerald: { from: "#059669", to: "#0d9488", btn: "#059669", btnHover: "#047857", ring: "rgba(5,150,105,0.4)" },
  sky:     { from: "#0284c7", to: "#4f46e5", btn: "#0284c7", btnHover: "#0369a1", ring: "rgba(2,132,199,0.4)" },
};

function applyTheme(preset: string) {
  const t = THEME_VARS[preset] ?? THEME_VARS["indigo"];
  const root = document.documentElement;
  root.style.setProperty("--brand-from", t.from);
  root.style.setProperty("--brand-to", t.to);
  root.style.setProperty("--brand-btn", t.btn);
  root.style.setProperty("--brand-btn-hover", t.btnHover);
  root.style.setProperty("--brand-ring", t.ring);
}

interface SiteSettingsContextValue {
  settings: SiteSettings;
  refresh: () => void;
}

const SiteSettingsContext = createContext<SiteSettingsContextValue>({
  settings: defaults,
  refresh: () => {},
});

export function SiteSettingsProvider({ children }: { children: React.ReactNode }) {
  const [settings, setSettings] = useState<SiteSettings>(defaults);

  const refresh = useCallback(() => {
    fetch(SETTINGS_ENDPOINT)
      .then(async (r) => {
        if (r.ok) {
          const contentType = r.headers.get("content-type") || "";
          if (contentType.includes("application/json")) {
            return r.json();
          }
        }
        return null;
      })
      .then((d: any) => {
        if (d?.settings) {
          setSettings((prev) => ({ ...prev, ...d.settings }));
        }
      })
      .catch(() => {}); // use defaults on error
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    applyTheme(settings.theme_preset);
  }, [settings.theme_preset]);

  return React.createElement(
    SiteSettingsContext.Provider,
    { value: { settings, refresh } },
    children
  );
}

export function useSiteSettings(): SiteSettings {
  return useContext(SiteSettingsContext).settings;
}

export function useSiteSettingsRefresh(): () => void {
  return useContext(SiteSettingsContext).refresh;
}

export { THEME_VARS };
